import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, from } from 'rxjs';
import { ViveStatuses } from 'src/app/interfaces/vive-statuses';
import { ViveSubStatuses } from 'src/app/interfaces/vive-sub-statuses';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { NoteService } from 'src/app/services/note.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import {
  CompanyVendorDto,
  CompanyVendorDtoViveStatusEnum
} from 'src/app/shared/vive-api/vive-api-models';

@Component({
  selector: 'app-compliance-status-modal',
  templateUrl: './compliance-status-modal.component.html',
  styleUrls: ['./compliance-status-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceStatusModalComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private ModalService: NgbActiveModal,
    private CompanyVendorService: CompanyVendorService,
    private NoteService: NoteService,
    private UtilityService: UtilityService
  ) {}

  @Input() CompanyId?: number;
  @Input() VendorId?: number;
  @Input() CompanyVendor?: CompanyVendorDto;

  ViveStatuses: [
    null,
    'Approved',
    'Suspended',
    'Declined',
    'Inactive',
    'Registering',
    'Awaiting Payment',
    'Registered',
    'Reviewing',
    'Renewing',
    'Site Conditional'
    // 'Compliance Exempt',
    // 'Business Verified',
    // 'Non-Verified'
  ] = [
    null,
    'Approved',
    'Suspended',
    'Declined',
    'Inactive',
    'Registering',
    'Awaiting Payment',
    'Registered',
    'Reviewing',
    'Renewing',
    'Site Conditional'
  ];

  ViveSubStatuses: [
    null,
    'Cancelled Insurance',
    'Expired Insurance',
    'Expired License',
    'Never Paid',
    'No Renewal',
    'Waived Findings',
    'Fee Waived No Renewal'
  ] = [
    null,
    'Cancelled Insurance',
    'Expired Insurance',
    'Expired License',
    'Never Paid',
    'No Renewal',
    'Waived Findings',
    'Fee Waived No Renewal'
  ];

  CompanyVendorForm = new FormGroup({
    id: new FormControl<number | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true, validators: [Validators.required] }
    ),
    CompanyId: new FormControl<number | null>(null, {
      validators: [Validators.required]
    }),
    VendorId: new FormControl<number | null>(null, {
      validators: [Validators.required]
    }),
    viveStatus: new FormControl<ViveStatuses | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true, validators: [Validators.required] }
    ),
    viveSubStatus: new FormControl<ViveSubStatuses | undefined | null>(null)
  });

  NoteForm = new FormGroup({
    title: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    note: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    CompanyId: new FormControl<number | null>(null, {
      validators: [Validators.required]
    }),
    VendorId: new FormControl<number | null>(null, {
      validators: [Validators.required]
    }),
    isSensitive: new FormControl<boolean>(false, { nonNullable: true })
  });

  save() {
    const CompanyVendorErrors =
      this.UtilityService.generateValidationErrorsObject(
        this.CompanyVendorForm
      );

    if (CompanyVendorErrors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with one of the fields below.',
        { returnError: true }
      );
    }

    const NoteErrors = this.UtilityService.generateValidationErrorsObject(
      this.NoteForm
    );

    if (NoteErrors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with one of the fields below.',
        { returnError: true }
      );
    }

    const note = this.NoteForm.value;
    const companyVendor = this.CompanyVendorForm.value;

    const { viveStatus, viveSubStatus } = companyVendor;

    if (!viveSubStatus) {
      Object.assign(companyVendor, { viveSubStatus: null });
    }

    forkJoin([
      this.NoteService.saveNote(note),
      companyVendorsService.updateOneById({
        pathParams: { companyVendorId: companyVendor.id! },
        data: {
          viveStatus: viveStatus as CompanyVendorDtoViveStatusEnum,
          viveSubStatus
        }
      })
    ]).subscribe({
      next: ([Note, CompanyVendor]) => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved VIVE Status.'
        );

        if (this.CompanyVendor) {
          Object.assign(this.CompanyVendor, CompanyVendor);
        }

        this.ModalService.close([Note, this.CompanyVendor || CompanyVendor]);
      }
    });
  }

  close() {
    this.ModalService.dismiss();
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (this.CompanyVendor) {
      this.CompanyVendorForm.patchValue(
        Object.assign(this.CompanyVendor, {
          CompanyId: this.CompanyId,
          VendorId: this.VendorId
        })
      );
      this.NoteForm.patchValue({
        CompanyId: this.CompanyId,
        VendorId: this.VendorId
      });
    } else {
      from(
        companyVendorsService.findOneByCompanyAndVendor({
          pathParams: {
            companyId: this.CompanyId!,
            vendorId: this.VendorId!
          }
        })
      ).subscribe({
        next: (CompanyVendor) => {
          if (!CompanyVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client/Vendor information.',
              { returnError: true }
            );
          }

          this.CompanyVendor = CompanyVendor;
          this.CompanyVendorForm.patchValue(CompanyVendor);
          this.NoteForm.patchValue({
            CompanyId: this.CompanyId,
            VendorId: this.VendorId
          });
        }
      });
    }
  }
}
